import {Util} from 'yougile-util'
import {DataObjectId} from 'yougile-model'

type RequestLimitGuard = {[taskIdOrIdTask: string]: number}
type AlreadyRequestedGuard = {[taskIdOrIdTask: string]: boolean}
const maxRequestLimitPerId = 5

export class FetchWithDelay<T> {
  _requestLimitGuard: RequestLimitGuard = {}
  _alreadyRequestedGuard: AlreadyRequestedGuard = {}

  delay = 2_000
  timer: NodeJS.Timeout | undefined
  requestQueue: DataObjectId[] = []
  func: (ids: DataObjectId[]) => Promise<T>
  cb: (value: T) => void
  idCanBeSkipped?: (id: DataObjectId) => boolean

  constructor(
    func: (ids: DataObjectId[]) => Promise<T>,
    cb: (value: T) => void,
    idCanBeSkipped?: (id: DataObjectId) => boolean
  ) {
    this.func = func
    this.cb = cb
    this.idCanBeSkipped = idCanBeSkipped
  }

  public request(ids: DataObjectId[], debounced = false): void {
    this.requestQueue.push(...ids)

    if (this.timer) {
      return
    }

    if (!debounced) {
      void this.makeRequest()
    }

    this.runTimer()
  }

  private runTimer(): void {
    this.timer = setTimeout(this.tick, this.delay)
  }

  private tick = async (): Promise<void> => {
    await this.makeRequest()

    const f = this.getFilteredQueue()

    if (f.length) {
      this.runTimer()
    } else {
      this.timer = undefined
    }
  }

  private async makeRequest(): Promise<void> {
    const queue = this.getFilteredQueue()
    this.requestQueue = []

    if (!queue.length) {
      return
    }

    for (const id of queue) {
      this.incRequestLimitGuard(id)
      this.setAlreadyRequestedGuard(id, true)
    }

    const result = await this.func(queue)
    this.cb(result)

    for (const id of queue) {
      this.setAlreadyRequestedGuard(id, false)
    }
  }

  private getFilteredQueue(): DataObjectId[] {
    const queue = this.requestQueue.filter(id => {
      if (
        this.getRequestLimitGuard()[id] >= maxRequestLimitPerId ||
        this.getAlreadyRequestedGuard()[id]
      ) {
        return false
      }

      if (this.idCanBeSkipped?.(id)) {
        return false
      }

      return true
    })

    return Util.removeDuplicates(queue) as DataObjectId[]
  }

  private getRequestLimitGuard(): RequestLimitGuard {
    return this._requestLimitGuard
  }

  private incRequestLimitGuard(id: string): void {
    if (id) {
      const curr = this._requestLimitGuard[id]
      if (!curr) {
        this._requestLimitGuard[id] = 1
      } else {
        this._requestLimitGuard[id] = curr + 1
      }
    }
  }

  private getAlreadyRequestedGuard(): AlreadyRequestedGuard {
    return this._alreadyRequestedGuard
  }

  private setAlreadyRequestedGuard(id: string, value: boolean): void {
    this._alreadyRequestedGuard[id] = value
  }
}
